/**
 * This class prepares all basic fade in animations
 */
export default class FadeIn {

  /**
   * Fade In constructor
   * 
   * We have some default parameters, so anyone can use the animation straight away.
   * Otherwise, you can change one, two or all of them.
   * 
   * For it to trigger as we scroll, we have to use the method 'createScrollTrigger'. 
   * If you want it to work independently, you can use the method 'init'.
   * 
   * @param {HTMLElement} element
   * The element that we want to animate.
   * 
   * The x, y, duration and easing are part of an Object. As such, you can customize the ones you want
   * and leave the rest with the default values if you choose.
   * 
   * If you want to use only default values, leave the method as:
   **          FadeIn(element, {})
   * 
   * IMPORTANT: Remember to put these values between {} 
   * and to write them as an Object. 
   * Example: 
   **          FadeIn(element, {duration: 2, x: 10})
   * 
   * @param {number} x
   * The horizontal direction from which the element will appear.
   * 
   * If we want to fade in from the left, we'll use a negative number.
   * Example: 
   **         FadeIn(element, {x: -100})

   * If we want to fade in from the right, we'll use a positive number.
   * Example: 
   **         FadeIn(element, {x: 100})
   * 
   * @param {number} y
   * The vertical direction from which the element will appear.
   * 
   * If we want to fade in up, we'll use a negative number 
   * Example: 
   **         FadeIn(element, {y: -100})
   * 
   * If we want to fade in down, we'll use a positive number    
   * Example: 
   **         FadeIn(element, {y: 100})
   * 
   * 
   * @param {number} duration
   * Here we state the seconds we want the animation to last   
   * Example: 
   **         FadeIn(element, {duration: 1.5})
   * 
   * 
   * @param {string} easing
   * Here we state what kind of easing we want the animation to have. By default, we
   * have the 'Power0', that it's similar to a 'linear' easing. 
   * 
   * To change it, we have to pass the parameter as a string. Example:
   **         FadeIn(element, {easing: 'Power4'})
   *
   * Gsap offers a lot of different kind of easings, 
   * you can see and test them here: https://greensock.com/docs/v2/Easing
   * 
   * @param {boolean} scrollTrigger
   * The scrollTrigger parameter allows you to choose if you want the animation to be triggered by scroll,
   * or if you want it to be a standalone. By default, it's set to true. If you want to change it, 
   * you set it to false. Example: 
   **         FadeIn(element, {scrollTrigger: false})
   * @param {boolean} once
   * The once parameter defines if the scrollTrigger animation will only run once or if it'll repeat on entering.
   * By default, it's set to true. If you want to change it, 
   * you set it to false. Example: 
   **         FadeIn(element, {once: false})
   */
    constructor(element = this.element,
        {
            x = 0, y = 100, duration = 1, easing = 'Power0', scrollTrigger = true, once = true
        })
    {

      this.element = element;
      this.x = x;
      this.y = y;
      this.duration = duration;
      this.easing = easing;
      this.scrollTrigger = scrollTrigger;
      this.once = once;  
  }

/**
   * This method initializes the fade in animation
   */
    init() {
        const direction = this.directionSetter();
        const scrollTrigger = this.createScrollTrigger();

        gsap.fromTo(
            this.element,
            {
                ...direction.from,
                opacity: 0,
            },
            {
                ...direction.to,
                opacity: 1,
                duration: this.duration,
                ease: this.easing + '.easeIn',
                ...scrollTrigger,
                onComplete: () => {
                // Reset css prop
                    this.element.style.transform = "";
                },
            }
        );
    }

  /**
   * This method sets up the direction of the element
   * and returns an object to set on gsap props
   * @return {Object} Object with directions values
   */
    directionSetter() {
        return {
            from: {
                x: this.x,
                y: this.y
            },
            to: {
                x: 0,
                y: 0
            },
        };
    }

  /**
   * This method creates the scroll trigger of the element, 
   * so the animation starts when the element appears
   * @return {Object} 
   * Object with scrollTrigger values, if set to true,
   * else it returns empty
   */
    createScrollTrigger() {
        const toggle = this.toggleActionsSetter();
        
        return this.scrollTrigger ? {
            scrollTrigger: {
                trigger: this.element,
                start: "top 60%",
                end: "bottom 10%",
                ...toggle,
                // markers: true,
            }
        } :
            ''
    }

  /**
   * @return {Object} 
   * This method returns the toggleActions if the parameter 'once' is set to false
   * Otherwise, it'll return 'once: true', so the scrollTrigger animation runs only once
   */
    toggleActionsSetter() {
        return this.once ?
            {
                once: true
            } :
            {
                toggleActions: 'play reverse play reverse'
            }
    }
}

